import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { X, CircleNotch } from '@phosphor-icons/react';
import studentsService from '../services/studentsService';

const EXIT_STATUSES = [
  { value: 'graduated', label: 'Graduated' },
  { value: 'transferred', label: 'Transferred' },
  { value: 'withdrawn', label: 'Withdrawn' },
];

// Year-end promotion: pick the class a roster is currently in, then decide,
// per student, whether they move to a new class or leave the school
// (graduated/transferred/withdrawn). Mirrors what a "Promote Students"
// wizard does in most school ERPs, submitted in one call to POST /students/promote.
function PromoteStudentsModal({ open, onClose, classes, onDone }) {
  const [sourceClassId, setSourceClassId] = useState('');
  const [roster, setRoster] = useState([]);
  const [loadingRoster, setLoadingRoster] = useState(false);
  const [bulkAction, setBulkAction] = useState('');
  const [selections, setSelections] = useState({}); // student_id -> action value
  const [submitting, setSubmitting] = useState(false);

  const loadRoster = useCallback(() => {
    if (!sourceClassId) {
      setRoster([]);
      return;
    }
    setLoadingRoster(true);
    studentsService.listStudents({ class_id: sourceClassId, status: 'active', limit: 100 })
      .then(data => {
        setRoster(data.items || []);
        setSelections({});
        setBulkAction('');
      })
      .catch(() => toast.error('Failed to load class roster'))
      .finally(() => setLoadingRoster(false));
  }, [sourceClassId]);

  useEffect(() => {
    loadRoster();
  }, [loadRoster]);

  useEffect(() => {
    if (!open) {
      setSourceClassId('');
      setRoster([]);
      setSelections({});
      setBulkAction('');
    }
  }, [open]);

  if (!open) return null;

  const otherClasses = classes.filter(c => c.id !== sourceClassId);

  const applyBulkAction = (value) => {
    setBulkAction(value);
    if (!value) return;
    const next = {};
    roster.forEach(s => { next[s.id] = value; });
    setSelections(next);
  };

  const handleSubmit = async () => {
    const actions = Object.entries(selections)
      .filter(([, value]) => value)
      .map(([student_id, value]) => {
        if (EXIT_STATUSES.some(s => s.value === value)) {
          return { student_id, status: value };
        }
        return { student_id, target_class_id: value };
      });

    if (actions.length === 0) {
      toast.error('Choose an action for at least one student');
      return;
    }

    setSubmitting(true);
    try {
      const result = await studentsService.promoteStudents(actions);
      const promotedCount = result.promoted?.length || 0;
      const statusCount = result.status_changed?.length || 0;
      const errorCount = result.errors?.length || 0;

      if (promotedCount || statusCount) {
        toast.success(`${promotedCount} promoted, ${statusCount} moved to a new status`);
      }
      if (errorCount) {
        toast.error(`${errorCount} failed: ${result.errors.map(e => e.error).join('; ')}`);
      }
      onDone();
      if (!errorCount) onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Promotion failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-lg" onClick={e => e.stopPropagation()} style={{ maxHeight: '90vh' }}>
        <div className="modal-header">
          <h2>Promote Students</h2>
          <button className="modal-close" onClick={onClose}><X size={20} /></button>
        </div>

        <div className="modal-content" style={{ maxHeight: 'calc(90vh - 130px)', overflowY: 'auto' }}>
          <div className="form-group">
            <label>Class to promote from</label>
            <select value={sourceClassId} onChange={e => setSourceClassId(e.target.value)} disabled={submitting}>
              <option value="">Select a class</option>
              {classes.map(cls => (
                <option key={cls.id} value={cls.id}>{cls.name}</option>
              ))}
            </select>
          </div>

          {loadingRoster && <div className="loading">Loading roster...</div>}

          {!loadingRoster && sourceClassId && roster.length === 0 && (
            <div className="empty-state">No active students in this class</div>
          )}

          {!loadingRoster && roster.length > 0 && (
            <>
              <div className="form-group">
                <label>Apply to everyone (individual rows can still be changed below)</label>
                <select value={bulkAction} onChange={e => applyBulkAction(e.target.value)} disabled={submitting}>
                  <option value="">-- choose an action --</option>
                  {otherClasses.map(cls => (
                    <option key={cls.id} value={cls.id}>Promote to {cls.name}</option>
                  ))}
                  {EXIT_STATUSES.map(s => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>

              <table className="data-table">
                <thead>
                  <tr>
                    <th>Student</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {roster.map(s => (
                    <tr key={s.id}>
                      <td>{s.full_name}</td>
                      <td>
                        <select
                          value={selections[s.id] || ''}
                          onChange={e => setSelections(prev => ({ ...prev, [s.id]: e.target.value }))}
                          disabled={submitting}
                        >
                          <option value="">No change</option>
                          {otherClasses.map(cls => (
                            <option key={cls.id} value={cls.id}>Promote to {cls.name}</option>
                          ))}
                          {EXIT_STATUSES.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>

        <div className="modal-actions">
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={submitting}>Cancel</button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={submitting || roster.length === 0}
          >
            {submitting ? (
              <span className="button-loading">
                <CircleNotch size={16} weight="fill" className="spinner" />
                Applying...
              </span>
            ) : (
              'Apply Promotions'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default PromoteStudentsModal;
